import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { shippingApi } from '../services/api';
import { ShippingRecord } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { formatVietnamCurrency } from '../utils/numberFormatter';
import { formatDate } from '../utils/dateFormatter';
import { 
  sortRecords, 
  getUniqueValues, 
  getDestinationDisplay,
  SortField,
  SortDirection 
} from '../utils/shipping/shippingSort';
import { exportShippingRecordsToExcel } from '../utils/shipping/exportToExcel';

// Helper function to get default date range
const getDefaultDateRange = () => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const fiveDaysFromNow = new Date(now);
    fiveDaysFromNow.setDate(now.getDate() + 5);
    
    // End date is at least 5 days from now, or end of month (whichever is later)
    const endDate = fiveDaysFromNow > endOfMonth ? fiveDaysFromNow : endOfMonth;
    
 return {
  startDate: startOfMonth.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0]
  };
};

const ShippingPage: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useLanguage();
  const [records, setRecords] = useState<ShippingRecord[]>([]);
    const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(true);
    
    const defaultDateRange = getDefaultDateRange();
  const [filters, setFilters] = useState({
        licensePlate: '',
        shippingAddress: '',
        province: '',
    startDate: defaultDateRange.startDate,
        endDate: defaultDateRange.endDate
    });
    
    const [processingSortField, setProcessingSortField] = useState<SortField>('date');
    const [processingSortDirection, setProcessingSortDirection] = useState<SortDirection>('desc');
    const [createdSortField, setCreatedSortField] = useState<SortField>('date');
    const [createdSortDirection, setCreatedSortDirection] = useState<SortDirection>('desc');

    const fetchRecords = async () => {
        try {
            const response = await shippingApi.getAll();
            setRecords(response.data);
        } catch (error) {
            console.error('Error fetching records:', error);
        } finally {
            setLoading(false);
        }
    };

    // Handle sort for processing records
    const handleProcessingSort = (field: SortField) => {
        if (processingSortField === field) {
            setProcessingSortDirection(processingSortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setProcessingSortField(field);
            setProcessingSortDirection('asc');
        }
    };

    // Handle sort for created records
    const handleCreatedSort = (field: SortField) => {
        if (createdSortField === field) {
            setCreatedSortDirection(createdSortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setCreatedSortField(field);
            setCreatedSortDirection('asc');
        }
    };

    // Sort indicator component
    const SortIndicator: React.FC<{ field: SortField; currentField: SortField; direction: SortDirection }> = ({ field, currentField, direction }) => {
        if (field !== currentField) return <span className="text-muted ms-1">⇅</span>;
        return <span className="ms-1">{direction === 'asc' ? '↑' : '↓'}</span>;
    };

    const handleAddNew = () => {
        navigate('/shipping/add');
    };

    const handleExportToExcel = () => {
        exportShippingRecordsToExcel(records, filters);
    };

    const handleEdit = (record: ShippingRecord) => {
        navigate(`/shipping/edit/${record.id}`);
    };

    // Add delete handler
    const handleDelete = async (id: number, isCreated: boolean = false) => {
    const message = isCreated
            ? 'Are you sure you want to delete this created record? It will be moved to deleted records and can be restored later.'
            : 'Are you sure you want to delete this processing record?';

        if (!window.confirm(message)) return;
      try {
 await shippingApi.delete(id);
            fetchRecords();
        } catch (error) {
        alert('Failed to delete record.');
        }
    };

    useEffect(() => {
        fetchRecords();
    }, []);

    if (loading) {
        return <div className="container mt-4"><div className="spinner-border"></div></div>;
    }

    const { licensePlates, provinces } = getUniqueValues(records);

    // Filter and sort processing records
    const processingRecords = sortRecords(
     records.filter(record => {
  const matchesLicense = !filters.licensePlate || record.licensePlate === filters.licensePlate;
   const matchesAddress = !filters.shippingAddress || record.shippingAddress.toLowerCase().includes(filters.shippingAddress.toLowerCase());
            const matchesProvince = !filters.province || record.provinceName === filters.province;

    // Date range filter is NOT applied to processing records - always show all pending records
   
        const isPending = (record as any).status === 'pending';
      return matchesLicense && matchesAddress && matchesProvince && isPending;
   }),
        processingSortField,
        processingSortDirection
    );

    // Filter and sort created records
    const createdRecords = sortRecords(
        records.filter(record => {
            const matchesLicense = !filters.licensePlate || record.licensePlate === filters.licensePlate;
         const matchesAddress = !filters.shippingAddress || record.shippingAddress.toLowerCase().includes(filters.shippingAddress.toLowerCase());
        const matchesProvince = !filters.province || record.provinceName === filters.province;
            
            // Date range filter
          const recordDate = new Date(record.shippingDate);
        const startDate = filters.startDate ? new Date(filters.startDate) : null;
       const endDate = filters.endDate ? new Date(filters.endDate) : null;
    const matchesDateRange = (!startDate || recordDate >= startDate) && (!endDate || recordDate <= endDate);
        
 const isCreated = (record as any).status === 'created';
            return matchesLicense && matchesAddress && matchesProvince && matchesDateRange && isCreated;
        }),
        createdSortField,
        createdSortDirection
    );

    return (
        <div className="w-100 p-2 p-md-4">
      {/* Header - Mobile Optimized */}
     <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 mb-md-4 gap-2">
    <h1 className="h3 h2-md mb-0">{t('shipping.records')}</h1>
     <div className="d-flex flex-column flex-sm-row gap-2 w-100 w-md-auto">
   <a href="/pricing/fee-structure" className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-center">
    <span className="d-none d-sm-inline">{t('manage.fee.structure')}</span>
<span className="d-inline d-sm-none">Fee Structure</span>
    </a>
       <button className="btn btn-primary btn-sm d-flex align-items-center justify-content-center" onClick={handleAddNew}>
          <i className="bi bi-plus-circle me-1"></i>
    <span className="d-none d-sm-inline">{t('add.shipping.record')}</span>
 <span className="d-inline d-sm-none">Add</span>
</button>
    </div>
      </div>

     {/* Filters - Collapsible on Mobile */}
  <div className="card mb-3 w-100">
       <div className="card-body">
           <div className="d-flex justify-content-between align-items-center mb-3">
      <h6 className="card-title mb-0">
     <i className="bi bi-funnel me-2"></i>
   {t('filters')}
       </h6>
            <button 
      className="btn btn-sm btn-outline-secondary d-md-none"
      onClick={() => setShowFilters(!showFilters)}
           >
      <i className={`bi bi-chevron-${showFilters ? 'up' : 'down'}`}></i>
       </button>
  </div>
    
          <div className={`${showFilters ? '' : 'd-none d-md-block'}`}>
       <div className="row g-2 g-md-3">
          <div className="col-12 col-sm-6 col-md-3">
       <label className="form-label small">{t('license.plate')}</label>
        <select
 className="form-select form-select-sm"
 value={filters.licensePlate}
         onChange={(e) => setFilters({ ...filters, licensePlate: e.target.value })}
       >
  <option value="">All License Plates</option>
 {licensePlates.map(plate => (
       <option key={plate} value={plate}>{plate}</option>
           ))}
      </select>
      </div>
    <div className="col-12 col-sm-6 col-md-3">
    <label className="form-label small">Shipping Address</label>
  <input
    type="text"
            className="form-control form-control-sm"
            placeholder="Filter address..."
  value={filters.shippingAddress}
       onChange={(e) => setFilters({ ...filters, shippingAddress: e.target.value })}
  />
       </div>
    <div className="col-12 col-sm-6 col-md-3">
  <label className="form-label small">Province</label>
        <select
     className="form-select form-select-sm"
          value={filters.province}
onChange={(e) => setFilters({ ...filters, province: e.target.value })}
    >
        <option value="">All Provinces</option>
    {provinces.map(province => (
        <option key={province} value={province}>{province}</option>
       ))}
   </select>
       </div>
      <div className="col-12 col-sm-6 col-md-3">
        <label className="form-label small">Start Date</label>
   <input
 type="date"
    className="form-control form-control-sm"
         value={filters.startDate}
    onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
   />
  </div>
   <div className="col-12 col-sm-6 col-md-3">
     <label className="form-label small">End Date</label>
    <input
          type="date"
             className="form-control form-control-sm"
value={filters.endDate}
     onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
    />
    </div>
       <div className="col-12 col-sm-6 col-md-9 d-flex align-items-end">
  <button 
 className="btn btn-outline-secondary btn-sm"
     onClick={() => {
    const defaultRange = getDefaultDateRange();
    setFilters({ 
  ...filters, 
  startDate: defaultRange.startDate, 
      endDate: defaultRange.endDate 
    });
}}
   >
        <i className="bi bi-arrow-counterclockwise me-1"></i>
   Reset Date
    </button>
   </div>
  </div>
    </div>
      </div>
      </div>

         {/* Processing (Pending) Records - Mobile Optimized */}
  <div className="card w-100 mb-3 mb-md-4">
  <div className="card-body p-2 p-md-3">
            <h5 className="card-title text-warning mb-3">
    <i className="bi bi-hourglass-split me-2"></i>
     {t('processing.records')} ({processingRecords.length})
   </h5>
     <div className="table-responsive">
  <table className="table table-sm table-hover mb-0">
         <thead className="table-light">
<tr>
           <th className="d-none d-md-table-cell" style={{ minWidth: '60px' }}>#</th>
 <th style={{ cursor: 'pointer', minWidth: '90px' }} onClick={() => handleProcessingSort('date')}>
   {t('date')}
        <SortIndicator field="date" currentField={processingSortField} direction={processingSortDirection} />
   </th>
    <th style={{ minWidth: '100px', cursor: 'pointer' }} onClick={() => handleProcessingSort('licensePlate')}>
      {t('truck')}
   <SortIndicator field="licensePlate" currentField={processingSortField} direction={processingSortDirection} />
    </th>
   <th className="d-none d-lg-table-cell" style={{ cursor: 'pointer' }} onClick={() => handleProcessingSort('destination')}>
 {t('destination')}
    <SortIndicator field="destination" currentField={processingSortField} direction={processingSortDirection} />
 </th>
  <th className="d-none d-xl-table-cell" style={{ cursor: 'pointer' }} onClick={() => handleProcessingSort('province')}>
   {t('destinationProvince')}
 <SortIndicator field="province" currentField={processingSortField} direction={processingSortDirection} />
      </th>
          <th className="d-none d-md-table-cell" style={{ cursor: 'pointer' }} onClick={() => handleProcessingSort('shippingCost')}>
   {t('shipping.cost')}
          <SortIndicator field="shippingCost" currentField={processingSortField} direction={processingSortDirection} />
    </th>
   <th className="d-none d-xl-table-cell" style={{ cursor: 'pointer' }} onClick={() => handleProcessingSort('prepaidAmount')}>
  {t('advance')}
     <SortIndicator field="prepaidAmount" currentField={processingSortField} direction={processingSortDirection} />
     </th>
         <th className="d-none d-xl-table-cell" style={{ cursor: 'pointer' }} onClick={() => handleProcessingSort('loadUnloadFee')}>
     {t('loading.fee')}
 <SortIndicator field="loadUnloadFee" currentField={processingSortField} direction={processingSortDirection} />
          </th>
  <th className="d-none d-xl-table-cell" style={{ cursor: 'pointer' }} onClick={() => handleProcessingSort('tollFee')}>
     {t('toll.fee')}
        <SortIndicator field="tollFee" currentField={processingSortField} direction={processingSortDirection} />
 </th>
       <th className="d-none d-xl-table-cell" style={{ cursor: 'pointer' }} onClick={() => handleProcessingSort('fuelCost')}>
  {t('fuel.cost')}
    <SortIndicator field="fuelCost" currentField={processingSortField} direction={processingSortDirection} />
      </th>
    <th className="d-none d-xl-table-cell" style={{ cursor: 'pointer' }} onClick={() => handleProcessingSort('fuelEfficiency')}>
     {t('fuel.efficiency')}
  <SortIndicator field="fuelEfficiency" currentField={processingSortField} direction={processingSortDirection} />
   </th>
<th className="d-none d-xl-table-cell" style={{ cursor: 'pointer' }} onClick={() => handleProcessingSort('rM2Fee')}>
{t('rm2')}
           <SortIndicator field="rM2Fee" currentField={processingSortField} direction={processingSortDirection} />
   </th>
        <th className="d-none d-xl-table-cell" style={{ cursor: 'pointer' }} onClick={() => handleProcessingSort('repairCost')}>
      {t('repair.cost')}
         <SortIndicator field="repairCost" currentField={processingSortField} direction={processingSortDirection} />
    </th>
 <th style={{ cursor: 'pointer', minWidth: '90px' }} onClick={() => handleProcessingSort('totalFee')}>
   {t('total.fee')}
     <SortIndicator field="totalFee" currentField={processingSortField} direction={processingSortDirection} />
       </th>
       {/* Mobile-visible columns after Total Fee */}
     <th className="d-lg-none" style={{ minWidth: '150px', cursor: 'pointer' }} onClick={() => handleProcessingSort('destination')}>
         {t('destination')}
         <SortIndicator field="destination" currentField={processingSortField} direction={processingSortDirection} />
       </th>
       <th className="d-lg-none" style={{ minWidth: '120px', cursor: 'pointer' }} onClick={() => handleProcessingSort('province')}>
    {t('destinationProvince')}
         <SortIndicator field="province" currentField={processingSortField} direction={processingSortDirection} />
       </th>
       <th className="d-lg-none" style={{ minWidth: '110px', cursor: 'pointer' }} onClick={() => handleProcessingSort('shippingCost')}>
         {t('shipping.cost')}
         <SortIndicator field="shippingCost" currentField={processingSortField} direction={processingSortDirection} />
       </th>
       <th className="d-lg-none" style={{ minWidth: '100px', cursor: 'pointer' }} onClick={() => handleProcessingSort('fuelCost')}>
 {t('fuel.cost')}
   <SortIndicator field="fuelCost" currentField={processingSortField} direction={processingSortDirection} />
       </th>
     <th className="d-lg-none" style={{ minWidth: '110px', cursor: 'pointer' }} onClick={() => handleProcessingSort('prepaidAmount')}>
       {t('advance')}
         <SortIndicator field="prepaidAmount" currentField={processingSortField} direction={processingSortDirection} />
       </th>
     <th className="d-lg-none" style={{ minWidth: '100px', cursor: 'pointer' }} onClick={() => handleProcessingSort('repairCost')}>
         {t('repair.cost')}
 <SortIndicator field="repairCost" currentField={processingSortField} direction={processingSortDirection} />
       </th>
     <th className="d-lg-none" style={{ minWidth: '120px', cursor: 'pointer' }} onClick={() => handleProcessingSort('fuelEfficiency')}>
         {t('fuel.efficiency')}
<SortIndicator field="fuelEfficiency" currentField={processingSortField} direction={processingSortDirection} />
       </th>
     <th style={{ minWidth: '120px' }}>{t('actions')}</th>
         </tr>
  </thead>
      <tbody>
         {processingRecords.map((record, index) => (
  <tr key={record.id}>
    <td className="d-none d-md-table-cell">{index + 1}</td>
    <td className="small">{formatDate(record.shippingDate)}</td>
   <td className="small fw-bold">{record.licensePlate}</td>
        <td className="d-none d-lg-table-cell small">{getDestinationDisplay(record)}</td>
   <td className="d-none d-xl-table-cell small">{record.provinceName || '-'}</td>
  <td className="d-none d-md-table-cell small">{typeof record.shippingCost === 'number' ? formatVietnamCurrency(record.shippingCost) : '-'}</td>
  <td className="d-none d-xl-table-cell small">{typeof record.prepaidAmount === 'number' ? formatVietnamCurrency(record.prepaidAmount) : '-'}</td>
   <td className="d-none d-xl-table-cell small">{typeof record.loadUnloadFee === 'number' ? formatVietnamCurrency(record.loadUnloadFee) : '-'}</td>
  <td className="d-none d-xl-table-cell small">{typeof record.tollFee === 'number' ? formatVietnamCurrency(record.tollFee) : '-'}</td>
        <td className="d-none d-xl-table-cell small">{typeof record.fuelCost === 'number' ? formatVietnamCurrency(record.fuelCost) : '-'}</td>
    <td className="d-none d-xl-table-cell">
    {record.fuelEfficiencyLitersPer100Km != null ? (
   <span className="badge bg-info">
{record.fuelEfficiencyLitersPer100Km.toFixed(2)} L/100km
</span>
 ) : (
    <span className="text-muted">{t('n.a')}</span>
  )}
 </td>
      <td className="d-none d-xl-table-cell small">{typeof record.rM2Fee === 'number' ? formatVietnamCurrency(record.rM2Fee) : '-'}</td>
  <td className="d-none d-xl-table-cell small">{typeof record.repairCost === 'number' ? formatVietnamCurrency(record.repairCost) : '-'}</td>
   <td className="small fw-bold text-primary">{typeof record.totalFee === 'number' ? formatVietnamCurrency(record.totalFee) : '-'}</td>
      <td className="d-lg-none small">{getDestinationDisplay(record)}</td>
      <td className="d-lg-none small">{record.provinceName || '-'}</td>
      <td className="d-lg-none small fw-bold text-primary">{typeof record.shippingCost === 'number' ? formatVietnamCurrency(record.shippingCost) : '-'}</td>
      <td className="d-lg-none small">{typeof record.fuelCost === 'number' ? formatVietnamCurrency(record.fuelCost) : '-'}</td>
      <td className="d-lg-none small">{typeof record.prepaidAmount === 'number' ? formatVietnamCurrency(record.prepaidAmount) : '-'}</td>
      <td className="d-lg-none small">{typeof record.repairCost === 'number' ? formatVietnamCurrency(record.repairCost) : '-'}</td>
      <td className="d-lg-none">
        {record.fuelEfficiencyLitersPer100Km != null ? (
    <span className="badge bg-info">
     {record.fuelEfficiencyLitersPer100Km.toFixed(2)} L/100km
          </span>
 ) : (
          <span className="text-muted">{t('n.a')}</span>
    )}
      </td>
     <td>
  <div className="btn-group d-none d-lg-flex" role="group">
    <button className="btn btn-outline-primary btn-sm" onClick={() => handleEdit(record)} title="Edit">
   <i className="bi bi-pencil"></i>
<span className="ms-1">{t('edit')}</span>
 </button>
  <button className="btn btn-outline-danger btn-sm" onClick={() => handleDelete(record.id, false)} title="Delete">
     <i className="bi bi-trash"></i>
    <span className="ms-1">{t('delete')}</span>
</button>
  </div>
   <div className="d-flex d-lg-none gap-1">
<button 
  className="btn btn-outline-primary btn-sm" 
 onClick={() => handleEdit(record)} 
  title="Edit"
     >
   <i className="bi bi-pencil" style={{ fontSize: '16px' }}></i>
     </button>
        <button 
    className="btn btn-outline-danger btn-sm" 
  onClick={() => handleDelete(record.id, false)} 
      title="Delete"
 >
<i className="bi bi-trash" style={{ fontSize: '16px' }}></i>
    </button>
 </div>
    </td>
  </tr>
  ))}

      </tbody>
    </table>
         </div>
       </div>
       </div>

    {/* Created Records - Mobile Optimized */}
     <div className="card w-100">
  <div className="card-body p-2 p-md-3">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 gap-2">
  <h5 className="card-title text-success mb-0">
   <i className="bi bi-check-circle me-2"></i>
    Created Records ({createdRecords.length})
          </h5>
             <button
        className="btn btn-success btn-sm d-flex align-items-center justify-content-center"
            onClick={handleExportToExcel}
    disabled={loading}
   >
     <i className="bi bi-file-earmark-excel me-1"></i>
       Export to Excel
     </button>
   </div>
 <div className="table-responsive">
     <table className="table table-sm table-hover mb-0">
   <thead className="table-light">
<tr>
     <th className="d-none d-md-table-cell" style={{ minWidth: '60px' }}>#</th>
      <th style={{ cursor: 'pointer', minWidth: '90px' }} onClick={() => handleCreatedSort('date')}>
Date
   <SortIndicator field="date" currentField={createdSortField} direction={createdSortDirection} />
    </th>
  <th style={{ minWidth: '100px', cursor: 'pointer' }} onClick={() => handleCreatedSort('licensePlate')}>
      {t('truck')}
   <SortIndicator field="licensePlate" currentField={createdSortField} direction={createdSortDirection} />
        </th>
    <th className="d-none d-lg-table-cell" style={{ cursor: 'pointer' }} onClick={() => handleCreatedSort('destination')}>
     {t('destination')}
    <SortIndicator field="destination" currentField={createdSortField} direction={createdSortDirection} />
 </th>
      <th className="d-none d-xl-table-cell" style={{ cursor: 'pointer' }} onClick={() => handleCreatedSort('province')}>
   {t('destinationProvince')}
    <SortIndicator field="province" currentField={createdSortField} direction={createdSortDirection} />
   </th>
   <th className="d-none d-md-table-cell" style={{ cursor: 'pointer' }} onClick={() => handleCreatedSort('shippingCost')}>
   {t('shipping.cost')}
              <SortIndicator field="shippingCost" currentField={createdSortField} direction={createdSortDirection} />
   </th>
       <th className="d-none d-xl-table-cell" style={{ cursor: 'pointer' }} onClick={() => handleCreatedSort('prepaidAmount')}>
             {t('advance')}
     <SortIndicator field="prepaidAmount" currentField={createdSortField} direction={createdSortDirection} />
  </th>
          <th className="d-none d-xl-table-cell" style={{ cursor: 'pointer' }} onClick={() => handleCreatedSort('loadUnloadFee')}>
      {t('loading.fee')}
     <SortIndicator field="loadUnloadFee" currentField={createdSortField} direction={createdSortDirection} />
   </th>
        <th className="d-none d-xl-table-cell" style={{ cursor: 'pointer' }} onClick={() => handleCreatedSort('tollFee')}>
 {t('toll.fee')}
   <SortIndicator field="tollFee" currentField={createdSortField} direction={createdSortDirection} />
      </th>
 <th className="d-none d-xl-table-cell" style={{ cursor: 'pointer' }} onClick={() => handleCreatedSort('fuelCost')}>
      {t('fuel.cost')}
     <SortIndicator field="fuelCost" currentField={createdSortField} direction={createdSortDirection} />
    </th>
   <th className="d-none d-xl-table-cell" style={{ cursor: 'pointer' }} onClick={() => handleCreatedSort('fuelEfficiency')}>
   {t('fuel.efficiency')}
  <SortIndicator field="fuelEfficiency" currentField={createdSortField} direction={createdSortDirection} />
    </th>
       <th className="d-none d-xl-table-cell" style={{ cursor: 'pointer' }} onClick={() => handleCreatedSort('rM2Fee')}>
      {t('rm2')}
       <SortIndicator field="rM2Fee" currentField={createdSortField} direction={createdSortDirection} />
   </th>
         <th className="d-none d-xl-table-cell" style={{ cursor: 'pointer' }} onClick={() => handleCreatedSort('repairCost')}>
   {t('repair.cost')}
     <SortIndicator field="repairCost" currentField={createdSortField} direction={createdSortDirection} />
      </th>
   <th className="d-none d-lg-table-cell" style={{ cursor: 'pointer' }} onClick={() => handleCreatedSort('driverSalary')}>
      {t('driver.salary')}
   <SortIndicator field="driverSalary" currentField={createdSortField} direction={createdSortDirection} />
    </th>
     <th className="d-none d-lg-table-cell" style={{ cursor: 'pointer' }} onClick={() => handleCreatedSort('salaryRate')}>
 {t('salary.rate')}
     <SortIndicator field="salaryRate" currentField={createdSortField} direction={createdSortDirection} />
</th>
<th style={{ cursor: 'pointer', minWidth: '90px' }} onClick={() => handleCreatedSort('totalFee')}>
       {t('total.fee')}
     <SortIndicator field="totalFee" currentField={createdSortField} direction={createdSortDirection} />
 </th>
      {/* Mobile-visible columns after Total Fee */}
      <th className="d-lg-none" style={{ minWidth: '150px', cursor: 'pointer' }} onClick={() => handleCreatedSort('destination')}>
      {t('destination')}
         <SortIndicator field="destination" currentField={createdSortField} direction={createdSortDirection} />
       </th>
       <th className="d-lg-none" style={{ minWidth: '120px' }} onClick={() => handleCreatedSort('province')}>
         {t('destinationProvince')}
         <SortIndicator field="province" currentField={createdSortField} direction={createdSortDirection} />
       </th>
       <th className="d-lg-none" style={{ minWidth: '110px' }} onClick={() => handleCreatedSort('shippingCost')}>
       {t('shipping.cost')}
  <SortIndicator field="shippingCost" currentField={createdSortField} direction={createdSortDirection} />
 </th>
      <th className="d-lg-none" style={{ minWidth: '100px' }} onClick={() => handleCreatedSort('fuelCost')}>
 {t('fuel.cost')}
   <SortIndicator field="fuelCost" currentField={createdSortField} direction={createdSortDirection} />
    </th>
       <th className="d-lg-none" style={{ minWidth: '110px' }} onClick={() => handleCreatedSort('prepaidAmount')}>
  {t('advance')}
         <SortIndicator field="prepaidAmount" currentField={createdSortField} direction={createdSortDirection} />
       </th>
       <th className="d-lg-none" style={{ minWidth: '100px' }} onClick={() => handleCreatedSort('repairCost')}>
         {t('repair.cost')}
       <SortIndicator field="repairCost" currentField={createdSortField} direction={createdSortDirection} />
       </th>
       <th className="d-lg-none" style={{ minWidth: '120px' }} onClick={() => handleCreatedSort('fuelEfficiency')}>
         {t('fuel.efficiency')}
     <SortIndicator field="fuelEfficiency" currentField={createdSortField} direction={createdSortDirection} />
 </th>
 <th style={{ minWidth: '120px' }}>{t('actions')}</th>
       </tr>
 </thead>
    <tbody>
     {createdRecords.map((record, index) => (
 <tr key={record.id}>
   <td className="d-none d-md-table-cell">{index + 1}</td>
<td className="small">{formatDate(record.shippingDate)}</td>
      <td className="small fw-bold">{record.licensePlate}</td>
  <td className="d-none d-lg-table-cell small">{getDestinationDisplay(record)}</td>
        <td className="d-none d-xl-table-cell small">{record.provinceName || '-'}</td>
          <td className="d-none d-md-table-cell small">{typeof record.shippingCost === 'number' ? formatVietnamCurrency(record.shippingCost) : '-'}</td>
      <td className="d-none d-xl-table-cell small">{typeof record.prepaidAmount === 'number' ? formatVietnamCurrency(record.prepaidAmount) : '-'}</td>
     <td className="d-none d-xl-table-cell small">{typeof record.loadUnloadFee === 'number' ? formatVietnamCurrency(record.loadUnloadFee) : '-'}</td>
       <td className="d-none d-xl-table-cell small">{typeof record.tollFee === 'number' ? formatVietnamCurrency(record.tollFee) : '-'}</td>
       <td className="d-none d-xl-table-cell small">{typeof record.fuelCost === 'number' ? formatVietnamCurrency(record.fuelCost) : '-'}</td>
       <td className="d-none d-xl-table-cell">
      {record.fuelEfficiencyLitersPer100Km != null ? (
<span className="badge bg-info">
          {record.fuelEfficiencyLitersPer100Km.toFixed(2)} L/100km
        </span>
   ) : (
           <span className="text-muted">{t('n.a')}</span>
 )}
      </td>
          <td className="d-none d-xl-table-cell small">{typeof record.rM2Fee === 'number' ? formatVietnamCurrency(record.rM2Fee) : '-'}</td>
         <td className="d-none d-xl-table-cell small">{typeof record.repairCost === 'number' ? formatVietnamCurrency(record.repairCost) : '-'}</td>
  <td className="d-none d-lg-table-cell small">{typeof record.driverSalary === 'number' ? formatVietnamCurrency(record.driverSalary) : <span className="text-muted">...</span>}</td>
   <td className="d-none d-lg-table-cell small">{typeof record.salaryRate === 'number' ? (record.salaryRate * 100).toFixed(2) + '%' : <span className="text-muted">...</span>}</td>
<td className="small fw-bold text-success">{typeof record.totalFee === 'number' ? formatVietnamCurrency(record.totalFee) : '-'}</td>
      <td className="d-lg-none small">{getDestinationDisplay(record)}</td>
      <td className="d-lg-none small">{record.provinceName || '-'}</td>
      <td className="d-lg-none small fw-bold text-success">{typeof record.shippingCost === 'number' ? formatVietnamCurrency(record.shippingCost) : '-'}</td>
     <td className="d-lg-none small">{typeof record.fuelCost === 'number' ? formatVietnamCurrency(record.fuelCost) : '-'}</td>
      <td className="d-lg-none small">{typeof record.prepaidAmount === 'number' ? formatVietnamCurrency(record.prepaidAmount) : '-'}</td>
      <td className="d-lg-none small">{typeof record.repairCost === 'number' ? formatVietnamCurrency(record.repairCost) : '-'}</td>
      <td className="d-lg-none">
        {record.fuelEfficiencyLitersPer100Km != null ? (
    <span className="badge bg-info">
     {record.fuelEfficiencyLitersPer100Km.toFixed(2)} L/100km
          </span>
 ) : (
          <span className="text-muted">{t('n.a')}</span>
    )}
      </td>
     <td>
  <div className="btn-group d-none d-lg-flex" role="group">
    <button className="btn btn-outline-primary btn-sm" onClick={() => handleEdit(record)} title="Edit">
   <i className="bi bi-pencil"></i>
       <span className="ms-1">{t('edit')}</span>
 </button>
  <button className="btn btn-outline-danger btn-sm" onClick={() => handleDelete(record.id, true)} title="Delete">
 <i className="bi bi-trash"></i>
 <span className="ms-1">{t('delete')}</span>
  </button>
  </div>
   {/* Mobile buttons */}
       <div className="d-flex d-lg-none gap-1">
    <button 
className="btn btn-outline-primary btn-sm" 
      onClick={() => handleEdit(record)} 
     title="Edit"
  >
 <i className="bi bi-pencil" style={{ fontSize: '16px' }}></i>
   </button>
<button 
    className="btn btn-outline-danger btn-sm" 
  onClick={() => handleDelete(record.id, true)} 
    title="Delete"
   >
 <i className="bi bi-trash" style={{ fontSize: '16px' }}></i>
    </button>
 </div>
     </td>
</tr>
 ))}
</tbody>
 </table>
  </div>
    </div>
    </div>
     </div>
  );
};

export default ShippingPage;